import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Pencil,
  Power,
  Plus,
  Users,
  ShoppingBag,
  ExternalLink,
} from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { PageLoading, PageNotFound } from '@/components/ui/PageAsyncState'
import { useCan } from '@/features/auth/AuthContext'
import {
  getCustomerApi,
  patchCustomerApi,
  createBuyerApi,
  patchBuyerApi,
  type CustomerDetail,
  type CustomerBuyer,
} from '@/services/customers'
import { apiFetch } from '@/services/apiFetch'
import { formatDocumentError } from '@/utils/documentErrorMessages'

type TabType = 'buyers' | 'orders'

interface CustomerOrderRow {
  id: string
  code: string
  buyer_name: string | null
  order_date: string
  requested_delivery_date: string | null
  status: 'draft' | 'confirmed' | 'cancelled'
  line_count: number
  total_quantity: number
  customer_pos: string[]
}

const ORDER_STATUS_CONFIG: Record<
  string,
  { tone: 'neutral' | 'success' | 'danger'; label: string }
> = {
  draft: { tone: 'neutral', label: 'Nháp' },
  confirmed: { tone: 'success', label: 'Đã xác nhận' },
  cancelled: { tone: 'danger', label: 'Đã hủy' },
}

export function CustomerDetailPage() {
  const { id = '' } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const canWrite = useCan('sales.write')

  const [customer, setCustomer] = useState<CustomerDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionError, setActionError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>('buyers')

  // Orders tab state
  const [orders, setOrders] = useState<CustomerOrderRow[]>([])
  const [loadingOrders, setLoadingOrders] = useState(false)

  // Modals
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false)
  const [editCustName, setEditCustName] = useState('')
  const [editCustNote, setEditCustNote] = useState('')
  const [custModalSubmitting, setCustModalSubmitting] = useState(false)
  const [custModalError, setCustModalError] = useState<string | null>(null)

  const [showAddBuyerModal, setShowAddBuyerModal] = useState(false)
  const [buyerName, setBuyerName] = useState('')
  const [buyerNote, setBuyerNote] = useState('')
  const [buyerSubmitting, setBuyerSubmitting] = useState(false)
  const [buyerModalError, setBuyerModalError] = useState<string | null>(null)

  const [editingBuyer, setEditingBuyer] = useState<CustomerBuyer | null>(null)
  const [editBuyerName, setEditBuyerName] = useState('')
  const [editBuyerNote, setEditBuyerNote] = useState('')

  async function loadCustomer() {
    setLoading(true)
    setActionError(null)
    try {
      const data = await getCustomerApi(id)
      setCustomer(data)
      if (data && searchParams.get('edit') === 'true' && canWrite) {
        setEditCustName(data.name)
        setEditCustNote(data.note ?? '')
        setShowEditCustomerModal(true)
      }
    } catch (err: unknown) {
      const formatted = formatDocumentError(err)
      setActionError(formatted.message)
    } finally {
      setLoading(false)
    }
  }

  async function loadOrders() {
    if (!id) return
    setLoadingOrders(true)
    try {
      const res = await apiFetch(`/api/sales-orders?customerId=${encodeURIComponent(id)}`)
      if (res.ok) {
        const data = await res.json()
        setOrders(data)
      }
    } catch (err) {
      console.error('Failed to load customer sales orders', err)
    } finally {
      setLoadingOrders(false)
    }
  }

  useEffect(() => {
    void loadCustomer()
  }, [id])

  useEffect(() => {
    if (activeTab === 'orders') {
      void loadOrders()
    }
  }, [activeTab, id])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (showEditCustomerModal) setShowEditCustomerModal(false)
        if (showAddBuyerModal) setShowAddBuyerModal(false)
        if (editingBuyer) setEditingBuyer(null)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [showEditCustomerModal, showAddBuyerModal, editingBuyer])

  async function handleToggleStatus() {
    if (!customer) return
    const newStatus = customer.status === 'active' ? 'inactive' : 'active'
    setActionError(null)
    try {
      await patchCustomerApi(customer.id, { status: newStatus })
      await loadCustomer()
    } catch (err: unknown) {
      const formatted = formatDocumentError(err)
      setActionError(formatted.message)
    }
  }

  async function handleUpdateCustomerSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!customer) return
    const trimmedName = editCustName.trim()
    if (!trimmedName) {
      setCustModalError('Tên khách hàng không được để trống.')
      return
    }

    setCustModalSubmitting(true)
    setCustModalError(null)
    try {
      await patchCustomerApi(customer.id, {
        name: trimmedName,
        note: editCustNote.trim() || null,
      })
      setShowEditCustomerModal(false)
      await loadCustomer()
    } catch (err: unknown) {
      const formatted = formatDocumentError(err)
      setCustModalError(formatted.message)
    } finally {
      setCustModalSubmitting(false)
    }
  }

  async function handleAddBuyerSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!customer) return
    const trimmedName = buyerName.trim()
    if (!trimmedName) {
      setBuyerModalError('Tên người mua không được để trống.')
      return
    }

    setBuyerSubmitting(true)
    setBuyerModalError(null)
    try {
      await createBuyerApi(customer.id, {
        name: trimmedName,
        note: buyerNote.trim() || null,
      })
      setShowAddBuyerModal(false)
      setBuyerName('')
      setBuyerNote('')
      await loadCustomer()
    } catch (err: unknown) {
      const formatted = formatDocumentError(err)
      setBuyerModalError(formatted.message)
    } finally {
      setBuyerSubmitting(false)
    }
  }

  async function handleUpdateBuyerSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!customer || !editingBuyer) return
    const trimmedName = editBuyerName.trim()
    if (!trimmedName) {
      setBuyerModalError('Tên người mua không được để trống.')
      return
    }

    setBuyerSubmitting(true)
    setBuyerModalError(null)
    try {
      await patchBuyerApi(customer.id, editingBuyer.id, {
        name: trimmedName,
        note: editBuyerNote.trim() || null,
      })
      setEditingBuyer(null)
      await loadCustomer()
    } catch (err: unknown) {
      const formatted = formatDocumentError(err)
      setBuyerModalError(formatted.message)
    } finally {
      setBuyerSubmitting(false)
    }
  }

  async function handleToggleBuyerStatus(buyer: CustomerBuyer) {
    if (!customer) return
    const newStatus = buyer.status === 'active' ? 'inactive' : 'active'
    setActionError(null)
    try {
      await patchBuyerApi(customer.id, buyer.id, { status: newStatus })
      await loadCustomer()
    } catch (err: unknown) {
      const formatted = formatDocumentError(err)
      setActionError(formatted.message)
    }
  }

  if (loading) {
    return <PageLoading label="Đang tải thông tin khách hàng..." />
  }

  if (!customer) {
    return (
      <PageNotFound
        title="Không tìm thấy khách hàng"
        description={`Khách hàng với mã/id "${id}" không tồn tại hoặc đã bị xóa.`}
        action={
          <Button onClick={() => navigate('/sales/customers')}>
            Về danh sách khách hàng
          </Button>
        }
      />
    )
  }

  return (
    <div className="space-y-4">
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Sale', to: '/sales' },
          { label: 'Khách hàng', to: '/sales/customers' },
          { label: customer.code },
        ]}
      />

      {/* Header */}
      <PageHeader
        title={`${customer.code} — ${customer.name}`}
        subtitle={
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge
              tone={customer.status === 'active' ? 'success' : 'neutral'}
              label={customer.status === 'active' ? 'Đang hoạt động' : 'Ngừng hoạt động'}
            />
            <span className="text-slate-400">•</span>
            <span>{customer.note || 'Chưa có ghi chú'}</span>
          </div>
        }
        actions={
          canWrite ? (
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setEditCustName(customer.name)
                  setEditCustNote(customer.note ?? '')
                  setCustModalError(null)
                  setShowEditCustomerModal(true)
                }}
              >
                <Pencil className="h-4 w-4" />
                Sửa thông tin
              </Button>
              <Button
                variant={customer.status === 'active' ? 'danger' : 'secondary'}
                onClick={handleToggleStatus}
              >
                <Power className="h-4 w-4" />
                {customer.status === 'active' ? 'Ngừng hoạt động' : 'Kích hoạt lại'}
              </Button>
            </div>
          ) : null
        }
      />

      {/* Action error banner */}
      {actionError && (
        <div className="rounded-[var(--radius-md)] border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <p className="font-semibold">Lỗi thao tác:</p>
          <p>{actionError}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="-mb-px flex gap-6" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setActiveTab('buyers')}
            className={`flex items-center gap-2 border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'buyers'
                ? 'border-emerald-600 text-emerald-700'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
            }`}
          >
            <Users className="h-4 w-4" />
            Người mua (Buyers)
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
              {customer.buyers?.length ?? 0}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('orders')}
            className={`flex items-center gap-2 border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'orders'
                ? 'border-emerald-600 text-emerald-700'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
            }`}
          >
            <ShoppingBag className="h-4 w-4" />
            Đơn hàng (Orders)
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
              {customer.open_order_count ?? 0} mở
            </span>
          </button>
        </nav>
      </div>

      {/* Tab: Buyers */}
      {activeTab === 'buyers' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800">
              Danh sách người mua trực thuộc
            </h3>
            {canWrite && (
              <Button
                size="sm"
                onClick={() => {
                  setBuyerName('')
                  setBuyerNote('')
                  setBuyerModalError(null)
                  setShowAddBuyerModal(true)
                }}
              >
                <Plus className="h-3.5 w-3.5" />
                Thêm người mua
              </Button>
            )}
          </div>

          <div className="overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="w-12 px-2 py-3 text-center">STT</th>
                  <th className="px-4 py-3">Tên người mua</th>
                  <th className="px-4 py-3">Ghi chú</th>
                  <th className="px-4 py-3">Trạng thái</th>
                  <th className="px-4 py-3 text-right">Thao tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {!customer.buyers || customer.buyers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-slate-400 text-sm">
                      Chưa có người mua nào.
                    </td>
                  </tr>
                ) : (
                  customer.buyers.map((b, index) => (
                    <tr key={b.id} className="hover:bg-slate-50">
                      <td className="w-12 px-2 py-3 text-center tabular-nums">{index + 1}</td>
                      <td className="px-4 py-3 font-medium text-slate-900">{b.name}</td>
                      <td className="px-4 py-3 text-slate-500">{b.note || '—'}</td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          tone={b.status === 'active' ? 'success' : 'neutral'}
                          label={b.status === 'active' ? 'Đang hoạt động' : 'Ngừng dùng'}
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canWrite ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              className="text-xs text-amber-600 hover:text-amber-800 font-medium"
                              onClick={() => {
                                setEditingBuyer(b)
                                setEditBuyerName(b.name)
                                setEditBuyerNote(b.note ?? '')
                                setBuyerModalError(null)
                              }}
                            >
                              Sửa
                            </button>
                            <span className="text-slate-300">|</span>
                            <button
                              type="button"
                              className={`text-xs font-medium ${
                                b.status === 'active'
                                  ? 'text-red-600 hover:text-red-800'
                                  : 'text-emerald-600 hover:text-emerald-800'
                              }`}
                              onClick={() => handleToggleBuyerStatus(b)}
                            >
                              {b.status === 'active' ? 'Ngừng dùng' : 'Kích hoạt lại'}
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab: Orders */}
      {activeTab === 'orders' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800">
              Đơn hàng của khách hàng này
            </h3>
          </div>

          <div className="overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3">Mã đơn</th>
                  <th className="px-4 py-3">Người mua</th>
                  <th className="px-4 py-3">PO Khách hàng</th>
                  <th className="px-4 py-3">Ngày đặt</th>
                  <th className="px-4 py-3">Ngày giao Y/C</th>
                  <th className="px-4 py-3 text-right">Số dòng</th>
                  <th className="px-4 py-3 text-right">Tổng SL</th>
                  <th className="px-4 py-3">Trạng thái</th>
                  <th className="px-4 py-3 text-right">Chi tiết</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loadingOrders ? (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-slate-500">
                      Đang tải danh sách đơn hàng...
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-slate-400">
                      Khách hàng này chưa có đơn hàng nào.
                    </td>
                  </tr>
                ) : (
                  orders.map((o) => {
                    const st = ORDER_STATUS_CONFIG[o.status] || {
                      tone: 'neutral' as const,
                      label: o.status,
                    }
                    return (
                      <tr key={o.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-900">
                          {o.code}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{o.buyer_name || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-600">
                          {o.customer_pos?.length ? o.customer_pos.join(', ') : '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{o.order_date}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {o.requested_delivery_date || '—'}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                          {o.line_count ?? 0}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900">
                          {o.total_quantity?.toLocaleString('vi-VN') ?? 0}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge tone={st.tone} label={st.label} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                            onClick={() => navigate(`/sales/orders/${o.id}`)}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            Xem đơn
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal: Edit Customer */}
      {showEditCustomerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-[var(--radius-md)] bg-white p-6 shadow-xl">
            <h3 className="text-base font-semibold text-slate-900 mb-4">
              Sửa thông tin khách hàng
            </h3>
            {custModalError && (
              <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                {custModalError}
              </div>
            )}
            <form onSubmit={handleUpdateCustomerSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Tên khách hàng *
                </label>
                <input
                  type="text"
                  required
                  value={editCustName}
                  onChange={(e) => setEditCustName(e.target.value)}
                  className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-emerald-700"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Ghi chú</label>
                <textarea
                  rows={3}
                  value={editCustNote}
                  onChange={(e) => setEditCustNote(e.target.value)}
                  className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-emerald-700"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowEditCustomerModal(false)}
                  disabled={custModalSubmitting}
                >
                  Hủy
                </Button>
                <Button type="submit" disabled={custModalSubmitting}>
                  {custModalSubmitting ? 'Đang lưu...' : 'Lưu thay đổi'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Add Buyer */}
      {showAddBuyerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-[var(--radius-md)] bg-white p-6 shadow-xl">
            <h3 className="text-base font-semibold text-slate-900 mb-4">Thêm người mua</h3>
            {buyerModalError && (
              <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                {buyerModalError}
              </div>
            )}
            <form onSubmit={handleAddBuyerSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Tên người mua *
                </label>
                <input
                  type="text"
                  required
                  value={buyerName}
                  onChange={(e) => setBuyerName(e.target.value)}
                  placeholder="VD: Phòng Thu Mua - John Doe"
                  className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-emerald-700"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Ghi chú</label>
                <textarea
                  rows={3}
                  value={buyerNote}
                  onChange={(e) => setBuyerNote(e.target.value)}
                  placeholder="Thông tin liên hệ, email, số điện thoại..."
                  className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-emerald-700"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowAddBuyerModal(false)}
                  disabled={buyerSubmitting}
                >
                  Hủy
                </Button>
                <Button type="submit" disabled={buyerSubmitting}>
                  {buyerSubmitting ? 'Đang lưu...' : 'Lưu người mua'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Edit Buyer */}
      {editingBuyer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-[var(--radius-md)] bg-white p-6 shadow-xl">
            <h3 className="text-base font-semibold text-slate-900 mb-4">
              Sửa người mua: {editingBuyer.name}
            </h3>
            {buyerModalError && (
              <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                {buyerModalError}
              </div>
            )}
            <form onSubmit={handleUpdateBuyerSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Tên người mua *
                </label>
                <input
                  type="text"
                  required
                  value={editBuyerName}
                  onChange={(e) => setEditBuyerName(e.target.value)}
                  className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-emerald-700"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Ghi chú</label>
                <textarea
                  rows={3}
                  value={editBuyerNote}
                  onChange={(e) => setEditBuyerNote(e.target.value)}
                  className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-emerald-700"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setEditingBuyer(null)}
                  disabled={buyerSubmitting}
                >
                  Hủy
                </Button>
                <Button type="submit" disabled={buyerSubmitting}>
                  {buyerSubmitting ? 'Đang lưu...' : 'Lưu thay đổi'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
